/**
 * Button — primary interactive atom.
 *
 * Wraps MUI's ButtonBase for accessibility, ripple, and keyboard handling.
 * Styling is fully owned by us via Emotion (`styled` from @mui/material/styles),
 * which gives us access to the MUI theme and CSS variables.
 *
 * Why not extend MUI's <Button>?
 *   MUI's Button uses contained/outlined/text + a limited palette. Our API
 *   uses solid/outline/ghost + role-based color including "neutral" (which
 *   isn't in MUI's palette). Extending and overriding all that ends up more
 *   complex than building on ButtonBase directly.
 *
 * Component-level decisions captured in this file follow the rules in
 * tokens/decisions/01-sizing-spacing-borders.md:
 *   - fixed height per size (32 / 48 / 64) — no vertical padding math
 *   - horizontal padding scaled by size (8 / 16 / 24)
 *   - radius.m (8px), border.width.s (1px)
 *   - no box-shadow ever
 *
 * @format
 */

import {Children, forwardRef, isValidElement, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled, type Theme} from '@mui/material/styles';
import ButtonBase, {type ButtonBaseProps} from '@mui/material/ButtonBase';

import {text, iconPxForRole, type TypographyRole} from 'ui/theme/typography';
import {
  useSurfaceContext,
  type SurfaceContext as SurfaceContextValue,
} from 'ui/theme/surfaceContext';

import sizingTokens from 'ui/tokens/semantic/sizing.json';
import bordersTokens from 'ui/tokens/semantic/borders.json';
import {v} from 'ui/tokens/util';

import {Icon} from '../Icon';
import {Spinner} from '../Spinner';

/**
 * Convert a `#rrggbb` hex string to an `rgba(...)` form with explicit
 * alpha. Used by ghost-variant hover/active states — those overlay a
 * brand-tinted fill at low opacity so the background bleeds through,
 * which works on any host surface regardless of its colour (including
 * Modal headers tinted with the same role's container).
 */
function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Public enums. Stories, docs tables, and external consumers should import
 * these arrays instead of hand-typing the union literals — otherwise the
 * Variant×Color matrix and similar surfaces silently desync when we add or
 * rename a value. `as const` keeps the array literal narrow, and the types
 * below are derived from it so there is no second source of truth.
 */
export const BUTTON_SIZES = ['s', 'm', 'l'] as const;
export const BUTTON_VARIANTS = ['solid', 'outline', 'ghost'] as const;
export const BUTTON_COLORS = ['primary', 'good', 'bad', 'alert', 'neutral'] as const;

export type ButtonSize = (typeof BUTTON_SIZES)[number];
export type ButtonVariant = (typeof BUTTON_VARIANTS)[number];
export type ButtonColor = (typeof BUTTON_COLORS)[number];

export interface ButtonProps extends Omit<ButtonBaseProps, 'color'> {
  /** Visual weight. `solid` filled, `outline` border-only, `ghost` text-only. */
  variant?: ButtonVariant;

  /** Semantic role — communicates intent, not visual hue. */
  color?: ButtonColor;

  /**
   * Surface context override. Default is inherited from the enclosing
   * `<SurfaceContextProvider>` (which itself defaults to `'dark'`), so
   * a Button inside a Modal automatically picks `'light'`. Pass this
   * prop only when you need to break out of the inherited context —
   * e.g. a button that visually belongs to the dark base even though
   * it sits inside an overlay.
   *
   * - `'dark'`  → use the regular spectrum (`primary`, `on-primary`, …).
   * - `'light'` → use the `inverse-*` spectrum, designed for overlays.
   *
   * Applies to ALL colors (primary, good, bad, alert, neutral).
   */
  context?: SurfaceContextValue;

  /** Height: s=32, m=48, l=64. Matches control.height.{s,m,l}. */
  size?: ButtonSize;

  /** Stretches to fill the container width. Form contexts. */
  fullWidth?: boolean;

  /**
   * Whether to round the corners (`radius.m`). Default `true`. Set to
   * `false` when the button is composed inside another rounded container
   * that will clip its corners — segmented button groups, form footers
   * with overflow-clip, etc. In those cases the outer container owns the
   * rounding and the button must stay square.
   */
  rounded?: boolean;

  /**
   * Stabilises the button width across label changes. Pass every label the
   * button can show in any state; the button reserves space for the widest
   * one so toggling between states never resizes the button.
   *
   * Typical use: dropdown / toggle buttons where the label flips between
   * "Open menu" / "Close menu", "Show all" / "Hide all", etc.
   *
   * Implementation is CSS-only — every entry renders as a hidden ghost in
   * the same grid cell as the active label, and the cell auto-sizes to
   * the widest ghost. No measurement, no flicker.
   *
   * No effect in icon-only mode or with `fullWidth` (width is determined
   * by other constraints in both cases).
   */
  stableLabels?: string[];

  /**
   * Left icon. Pass a Material Symbols ligature name (string) for the
   * common case, or a React element (e.g. `<Icon name="..." style={...} />`)
   * when you need direct control over axes/animation/rotation. Common
   * for dropdown chevrons that rotate on open.
   */
  leftIcon?: string | ReactNode;
  /** Right icon. Same string-or-element shape as `leftIcon`. */
  rightIcon?: string | ReactNode;

  /**
   * Which icon slot shows the loading spinner. `false` (default) = no
   * loading. `'left'` / `'right'` = the matching slot's icon is
   * replaced with an animated spinner.
   *
   * Everything else about the button — label, colors, size, the OTHER
   * slot's icon — stays exactly as it was. The spinner inherits color,
   * size, and weight from the button's text styling.
   *
   * The button is non-interactive while loading is truthy.
   */
  loading?: 'left' | 'right' | false;

  /** Button label. */
  children?: ReactNode;
}

// ── Size matrix ─────────────────────────────────────────────────────────────
// Everything here resolves from semantic tokens. Editing button geometry
// happens in tokens/semantic/sizing.json and typography.json, not here.

const HEIGHTS = sizingTokens.control.height;
const PAD_X = sizingTokens.control['padding-x'];
const CONTROL_GAP = sizingTokens.control.gap;

/** Maps button size to the typography role used for its label. */
const LABEL_ROLE: Record<ButtonSize, TypographyRole> = {
  s: 'label.s',
  m: 'label.m',
  l: 'label.l',
};

/**
 * Icon font-size derived from the label role's line-height: small label has
 * 14px line-height → 14px icon; m → 16; l → 20. This locks icon optical
 * size to the text baseline so the icon and label share visual height.
 *
 * Driven entirely by typography tokens — if the label role changes, the
 * icon follows automatically.
 */
function iconPxForButtonSize(size: ButtonSize): number {
  // System rule (see theme/typography): Icon size = line-height of the
  // paired text role. Button delegates to the shared util so a typography
  // role update propagates here automatically.
  return iconPxForRole(LABEL_ROLE[size]);
}

const SIZE_STYLES: Record<
  ButtonSize,
  {height: string; px: string; gap: string; labelRole: TypographyRole; iconPx: number}
> = {
  s: {
    height: v(HEIGHTS.s),
    px: v(PAD_X.s),
    gap: v(CONTROL_GAP.s),
    labelRole: LABEL_ROLE.s,
    iconPx: iconPxForButtonSize('s'),
  },
  m: {
    height: v(HEIGHTS.m),
    px: v(PAD_X.m),
    gap: v(CONTROL_GAP.m),
    labelRole: LABEL_ROLE.m,
    iconPx: iconPxForButtonSize('m'),
  },
  l: {
    height: v(HEIGHTS.l),
    px: v(PAD_X.l),
    gap: v(CONTROL_GAP.l),
    labelRole: LABEL_ROLE.l,
    iconPx: iconPxForButtonSize('l'),
  },
};

// ── Color/variant matrix ────────────────────────────────────────────────────
// One source of truth: given (theme, variant, color, context), return the
// background, text, and border colors. Reads everything from
// `theme.palette` — see `src/theme/index.ts` for the full shape and
// `src/theme/paletteAugmentation.ts` for the TypeScript declarations.
//
// In `'light'` context every role lookup picks the `inverse*` field
// (`inverseMain`, `inverseContainer`, …) so the button picks up the
// light-context spectrum used by overlays like Modal headers.

interface VariantPalette {
  bg: string;
  bgHover: string;
  bgActive: string;
  text: string;
  border?: string;
  /** Background fill when the button is `disabled`. Variant-aware. */
  disabledBg: string;
  /** Text/icon color when disabled. */
  disabledText: string;
  /** Border color when an `outline` button is disabled. */
  disabledBorder: string;
}

function palette(theme: Theme, variant: ButtonVariant, color: ButtonColor): VariantPalette {
  // No more `light ? p.X.inverseY : p.X.Y` switching. The active
  // SurfaceContextProvider sets `data-color-scheme` on its wrapper,
  // and MUI's CSS-var cascade resolves every `palette.X.Y` here to the
  // correct context's value automatically.
  const p = theme.palette;

  const disabledBg = p.disabledStates.low;
  const disabledText = p.disabledStates.high;
  const disabledBorder = p.disabledStates.main;

  // Neutral uses surface tokens, not role tokens. Tier-2 fill, tier-3
  // hover/active — the surface ramp is now properly per-context after
  // Path-2 so this works in dark and light alike.
  if (color === 'neutral') {
    const onSurface = p.text.primary;
    const surface2 = p.surface.surface2;
    const surface3 = p.surface.surface3;

    if (variant === 'solid') {
      return {
        bg: surface2,
        bgHover: surface3,
        bgActive: surface3,
        text: onSurface,
        disabledBg,
        disabledText,
        disabledBorder,
      };
    }
    if (variant === 'outline') {
      return {
        bg: 'transparent',
        bgHover: surface2,
        bgActive: surface3,
        text: onSurface,
        border: surface2,
        disabledBg,
        disabledText,
        disabledBorder,
      };
    }
    // ghost
    return {
      bg: 'transparent',
      bgHover: surface2,
      bgActive: surface3,
      text: onSurface,
      disabledBg,
      disabledText,
      disabledBorder,
    };
  }

  // primary / good / bad / alert — same shape, picked from theme.palette.
  const group = p[color];
  const base = group.main;
  const onBase = group.contrastText;
  const hover = group.hover;
  const active = group.active;
  const container = group.container;
  const hoverCont = group.hoverContainer;

  if (variant === 'solid') {
    return {
      bg: base,
      bgHover: hover,
      bgActive: active,
      text: onBase,
      disabledBg,
      disabledText,
      disabledBorder,
    };
  }
  if (variant === 'outline') {
    return {
      bg: 'transparent',
      bgHover: container,
      bgActive: hoverCont,
      text: base,
      border: base,
      disabledBg,
      disabledText,
      disabledBorder,
    };
  }
  // ghost
  return {
    bg: 'transparent',
    bgHover: container,
    bgActive: hoverCont,
    text: base,
    disabledBg,
    disabledText,
    disabledBorder,
  };
}

// ── Styled component ────────────────────────────────────────────────────────

interface StyledRootProps {
  $variant: ButtonVariant;
  $color: ButtonColor;
  $size: ButtonSize;
  $fullWidth: boolean;
  $loading: boolean;
  /** User's explicit disabled prop — distinct from "disabled because loading". */
  $userDisabled: boolean;
  $iconOnly: boolean;
  $rounded: boolean;
}

const Root = styled(ButtonBase, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<StyledRootProps>(({
  theme,
  $variant,
  $color,
  $size,
  $fullWidth,
  $loading,
  $userDisabled,
  $iconOnly,
  $rounded,
}) => {
  const size = SIZE_STYLES[$size];
  const p = palette(theme, $variant, $color);
  const radius = $rounded ? parseInt(v(bordersTokens.radius.m)) : 0;
  const borderWidth = $variant === 'outline' ? v(bordersTokens.border.width.s) : '0';

  // Width rules — both flags can be on simultaneously:
  //   fullWidth=true                       → 100% of parent.
  //   iconOnly=true, fullWidth=false       → square (width = height).
  //   neither                              → auto (content-sized).
  // minWidth=height for iconOnly so it stays at least square even when
  // fullWidth stretches it and the inner icon is small.
  const width = $fullWidth ? '100%' : $iconOnly ? size.height : 'auto';
  const minWidth = $iconOnly ? size.height : 0;
  // Horizontal padding zeros out in icon-only mode — the lone icon centers
  // via flex regardless of fullWidth.
  const px = $iconOnly ? '0' : size.px;

  return {
    // Box model
    height: size.height,
    paddingLeft: px,
    paddingRight: px,
    width,
    minWidth,
    borderRadius: radius,
    border: `${borderWidth} solid ${p.border ?? 'transparent'}`,
    boxSizing: 'border-box',

    // Typography — pulled from semantic role label.{s,m,l}.
    // line-height is overridden to 1 so the label stays vertically centered
    // inside the fixed-height control regardless of the role's leading.
    ...text(size.labelRole),
    lineHeight: 1,
    whiteSpace: 'nowrap',

    // Color
    backgroundColor: p.bg,
    color: p.text,

    // Layout — center label and icons via flex, so adding/removing icons
    // never shifts the baseline.
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: size.gap,

    // Smoothness
    transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
      duration: theme.transitions.duration.shorter,
    }),

    cursor: $loading ? 'progress' : 'pointer',

    // States
    //
    // Solid / outline: hover shifts background tint to the role's hover
    // colour, keeps the border ring stable.
    //
    // Ghost: hover/active paint the ghost's TEXT colour (= the role's
    // brand main, e.g. `primary.primary`) as a low-opacity fill — 20%
    // on hover, 30% on active. Two reasons:
    //   1. Brand main always contrasts with both light and dark host
    //      surfaces, unlike `*-container` (which on a Modal header is
    //      identical to the header bg → invisible hover).
    //   2. Text + icon stay full-opacity / fully readable — the fill
    //      is semi-transparent so the surface bleeds through, the glyph
    //      doesn't fade.
    '&:hover':
      $variant === 'ghost'
        ? {backgroundColor: hexToRgba(p.text, 0.2)}
        : {backgroundColor: p.bgHover},
    '&:active':
      $variant === 'ghost'
        ? {backgroundColor: hexToRgba(p.text, 0.3)}
        : {backgroundColor: p.bgActive},
    '&:focus-visible': {
      outline: `${v(bordersTokens.border.width.m)} solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },

    // The `:disabled` / `.Mui-disabled` selector matches in two distinct
    // situations: (1) the user passed `disabled`, or (2) `loading` is on
    // and we forward it to ButtonBase to block clicks. The styles differ:
    //
    //   User-disabled → full disabled-palette repaint.
    //   Loading-only  → variant colors stay; only cursor + pointer-events
    //                   change. The button looks identical to its non-
    //                   loading self, just non-interactive.
    '&:disabled, &.Mui-disabled': $userDisabled
      ? {
          cursor: 'not-allowed',
          backgroundColor: $variant === 'solid' ? p.disabledBg : 'transparent',
          color: p.disabledText,
          borderColor: $variant === 'outline' ? p.disabledBorder : 'transparent',
          pointerEvents: 'none',
        }
      : {
          cursor: 'progress',
          pointerEvents: 'none',
        },
  };
});

// ── Component ───────────────────────────────────────────────────────────────

/**
 * Resolve a string-or-element icon prop to a renderable node.
 *
 * Strings become `<Icon name={...} />` — the common case. Anything else (a
 * ReactElement, a fragment, even a custom node) is returned as-is. This is
 * the escape hatch for callers who need to control axes, transforms, or
 * animation on the icon directly.
 *
 * Example — dropdown chevron that rotates on open:
 *   ```tsx
 *   <Button rightIcon={
 *     <Icon name="keyboard_arrow_down" style={{
 *       transform: open ? 'rotate(180deg)' : 'none',
 *       transition: 'transform 150ms',
 *     }} />
 *   }>Menu</Button>
 *   ```
 */
// ── Loading spinner ────────────────────────────────────────────────────────
// The spinner occupies an icon slot — there is no separate "loading state"
// that replaces the whole button content. Вращение живёт в атоме `Spinner`:
// раньше оно было здесь, и всё остальное в системе — плашки, карточки,
// строки — показывало вместо него статичный знак.

function renderSpinnerSlot(iconPx: number): ReactNode {
  // Подпись не нужна: рядом стоит собственный текст кнопки, и «загрузка»
  // вторым голосом только удлиняет чтение скринридером.
  return <Spinner size={iconPx} label={null} />;
}

function renderIconSlot(icon: string | ReactNode, iconPx: number): ReactNode {
  // Empty string is treated the same as null — "no icon". Lets Storybook
  // select controls use `''` as the off option without rendering a blank
  // glyph box.
  if (!icon) return null;
  if (typeof icon === 'string') return <Icon name={icon} size={iconPx} />;
  if (isValidElement(icon)) return icon;
  return icon;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'solid',
    color = 'primary',
    context: contextProp,
    size = 'm',
    fullWidth = false,
    rounded = true,
    stableLabels,
    leftIcon,
    rightIcon,
    loading = false,
    disabled,
    children,
    ...rest
  },
  ref,
) {
  // Resolve the effective surface context: explicit prop wins, else
  // inherit from the surrounding SurfaceContextProvider (default 'dark').
  const context = useSurfaceContext(contextProp);

  // Icon size = label line-height. Computed in SIZE_STYLES from the
  // typography token, so icon and label baseline always agree.
  const iconPx = SIZE_STYLES[size].iconPx;

  const isLoading = Boolean(loading);

  // Each slot independently: if it's the loading target, render the
  // spinner; otherwise render the icon prop (if any). The spinner occupies
  // a real slot, so an icon-only loading button still gets the square
  // geometry from iconOnly mode.
  const leftIconNode =
    loading === 'left' ? renderSpinnerSlot(iconPx) : renderIconSlot(leftIcon, iconPx);
  const rightIconNode =
    loading === 'right' ? renderSpinnerSlot(iconPx) : renderIconSlot(rightIcon, iconPx);

  // Detect icon-only mode: no label, at least one icon slot occupied —
  // either by an explicit icon prop or by a spinner.
  const hasLabel = Children.count(children) > 0;
  const hasAnyIcon = Boolean(leftIconNode) || Boolean(rightIconNode);
  const iconOnly = !hasLabel && hasAnyIcon;

  // Stable-width label: stack all possible labels in one grid cell so the
  // cell auto-sizes to the widest. Ghost copies are visually hidden but
  // still occupy space. Only applied when meaningful — not in iconOnly
  // mode (no label) or fullWidth (width is externally determined).
  const useStableWidth = !!stableLabels && stableLabels.length > 0 && !iconOnly && !fullWidth;

  const labelNode = useStableWidth ? (
    <Box
      component='span'
      style={{
        display: 'inline-grid',
        gridTemplateAreas: '"label"',
        alignItems: 'center',
        justifyItems: 'center',
      }}
    >
      {stableLabels!.map((label, i) => (
        <Box
          component='span'
          key={i}
          aria-hidden='true'
          style={{
            gridArea: 'label',
            visibility: 'hidden',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Box>
      ))}
      <Box component='span' style={{gridArea: 'label'}}>
        {children}
      </Box>
    </Box>
  ) : (
    children
  );

  return (
    <Root
      ref={ref}
      data-color-scheme={context}
      $variant={variant}
      $color={color}
      $size={size}
      $fullWidth={fullWidth}
      $loading={isLoading}
      $userDisabled={Boolean(disabled)}
      $iconOnly={iconOnly}
      $rounded={rounded}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      // Pass through ButtonBase props (onClick, type, href, focusRipple, etc.)
      // — ButtonBase handles ripple, keyboard, focus management.
      {...rest}
    >
      {leftIconNode}
      {labelNode}
      {rightIconNode}
    </Root>
  );
});
